import React, { FunctionComponent } from 'react';

import { Text } from 'components/chroma';
import { PaymentFrequency, PaymentMethod } from 'scenes/common/types';
import {
  toDayOfMonthWithOrdinal,
  toShortMonthFormat,
} from 'scenes/common/helpers/datetime';
import {
  toCurrencyWithSign,
  TWO_DECIMAL_PLACES,
} from 'scenes/common/helpers/currency';

import { PaymentConfirmationProps } from '../types';

const emailConfirmation = (
  paymentMethod: PaymentMethod,
  contactEmail: string
): JSX.Element => (
  <Text>
    A confirmation email has been sent to <strong>{contactEmail}</strong>.{' '}
    {paymentMethod === PaymentMethod.BPAY &&
      `This will include details of how to make your payment using BPAY.`}
  </Text>
);

export const ConfirmationText: FunctionComponent<Omit<
  PaymentConfirmationProps,
  'quoteNumber' | 'policyStartDate'
>> = ({
  receiptNumber,
  contactEmail,
  paymentMethod,
  paymentFrequency,
  paymentDate,
  firstInstalment,
  monthlyInstalment,
}) => (
  <>
    {emailConfirmation(paymentMethod, contactEmail)}

    {paymentMethod === PaymentMethod.BPAY && (
      <>
        <Text>
          When payment has been successfully processed, we will send your
          receipt and policy documentation to <strong>{contactEmail}</strong>.
        </Text>
        <Text>
          Note that the payment will appear under the name of{' '}
          <strong>Insurance Australia Limited (IAL)</strong> on your financial
          institution statement.
        </Text>
      </>
    )}

    {paymentMethod === PaymentMethod.CreditCard && Boolean(receiptNumber) && (
      <>
        <Text>
          Your receipt number is <strong>{receiptNumber}</strong>
        </Text>
        <Text>
          Note that the payment will appear under the name of{' '}
          <strong>Insurance Australia Limited (IAL)</strong> on your financial
          institution statement.
        </Text>
      </>
    )}

    {paymentMethod === PaymentMethod.CreditCard && !receiptNumber && (
      <>
        <Text>
          When payment has been successfully processed, we will send your
          receipt and policy documentation to <strong>{contactEmail}</strong>.
        </Text>
        <Text>
          Note that the payment will appear under the name of{' '}
          <strong>Insurance Australia Limited (IAL)</strong> on your financial
          institution statement.
        </Text>
      </>
    )}

    {paymentMethod === PaymentMethod.DirectDebit &&
      paymentFrequency === PaymentFrequency.Annually && (
        <>
          <Text>
            When payment has been successfully processed, we will send your
            receipt and policy documentation to <strong>{contactEmail}</strong>.
          </Text>
          <Text>
            Note that the payment will appear under the name of{' '}
            <strong>Insurance Australia Limited (IAL)</strong> on your financial
            institution statement.
          </Text>
        </>
      )}

    {paymentMethod === PaymentMethod.DirectDebit &&
      paymentFrequency === PaymentFrequency.Monthly && (
        <>
          <Text>
            Note that the payment will appear under the name of{' '}
            <strong>Insurance Australia Limited (IAL)</strong> on your financial
            institution statement.
          </Text>
          <Text>
            When payment has been successfully processed, we will send your
            receipt and policy documentation to <strong>{contactEmail}</strong>.
          </Text>
          <Text>
            We will debit the first instalment of{' '}
            <strong>
              {toCurrencyWithSign(firstInstalment, TWO_DECIMAL_PLACES)}
            </strong>{' '}
            from your nominated account within 3 business days of your selected
            billing date <strong>{toShortMonthFormat(paymentDate)}</strong>. All
            future instalments of{' '}
            <strong>
              {toCurrencyWithSign(monthlyInstalment, TWO_DECIMAL_PLACES)}
            </strong>{' '}
            will be debited on the{' '}
            <strong>{toDayOfMonthWithOrdinal(paymentDate)}</strong> day of each
            month.
          </Text>
        </>
      )}
    {paymentMethod === PaymentMethod.DirectDebit &&
      paymentFrequency === PaymentFrequency.Monthly && (
        <>
          <Text>
            Your Renewal invitation will also auto renew and continue on monthly
            instalments unless you contact us to change this to an annual
            payment.
          </Text>
        </>
      )}
  </>
);

export default ConfirmationText;
